import { useEffect, useRef, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { ymd, ukDateLong, DAYS_SHORT } from "@/lib/format";
import AppointmentDialog from "@/components/AppointmentDialog";
import BlockOutDialog from "@/components/BlockOutDialog";
import { BlockDetailDialog, TimeOffListDialog } from "@/components/BlockoutManager";
import { useAuth } from "@/context/AuthContext";
import { withOverride } from "@/lib/override";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Plus, Ban, ListX } from "lucide-react";

const DAY_START = 7 * 60;
const DAY_END = 22 * 60;
const PX = 1; // px per minute
const SNAP = 15;
const HEIGHT = (DAY_END - DAY_START) * PX;
const HOLD_MS = 350;
const KIND_LABEL = { break: "Break", holiday: "Holiday", sickness: "Sickness", other: "Blocked", extra: "Extra hours" };

const toMin = (t) => { const [h, m] = t.split(":").map(Number); return h * 60 + m; };
const toHHMM = (n) => `${String(Math.floor(n / 60)).padStart(2, "0")}:${String(n % 60).padStart(2, "0")}`;
const snap = (m) => Math.max(DAY_START, Math.min(DAY_END, DAY_START + Math.round((m - DAY_START) / SNAP) * SNAP));
const mondayOf = (d) => { const x = new Date(d); const day = (x.getDay() + 6) % 7; x.setDate(x.getDate() - day); x.setHours(0, 0, 0, 0); return x; };
const clip = (s, e) => ({ top: (Math.max(DAY_START, s) - DAY_START) * PX, height: (Math.min(DAY_END, e) - Math.max(DAY_START, s)) * PX });

function Shade({ x, compact, onOpen }) {
  const extra = x.kind === "extra";
  const bg = extra ? "rgba(16,185,129,0.12)" : "repeating-linear-gradient(135deg, #f5f5f4 0 6px, #e7e5e4 6px 12px)";
  return (
    <div data-testid={x.block ? `blockout-${x.block.id}` : `break-${x.key}`} title={x.label}
      onPointerDown={(e) => { if (!extra) e.stopPropagation(); }} onClick={(e) => { if (extra) return; e.stopPropagation(); if (x.block) onOpen(x.block); }}
      className={`absolute z-[2] overflow-hidden px-1.5 py-1 text-[11px] font-medium ${extra ? "text-emerald-800 border-l-2 border-emerald-500" : "text-stone-600 border-y border-stone-300/60"} ${compact ? "left-0 w-2 px-0" : "left-0 right-0"}`}
      style={{ ...clip(x.s, x.e), background: bg }}>
      {!compact && <span className="bg-white/80 rounded px-1 inline-flex items-center gap-1">{x.label}
        {extra && x.block && <button type="button" aria-label="Remove extra hours" data-testid={`remove-extra-${x.block.id}`} onPointerDown={(e) => e.stopPropagation()} onClick={(e) => { e.stopPropagation(); onOpen(x.block); }} className="text-emerald-900 font-bold px-1">×</button>}
      </span>}
    </div>
  );
}

function ApptBlock({ a, color, week, onPointerDown }) {
  const height = Math.max(22, (toMin(a.end) - toMin(a.start)) * PX);
  return (
    <div data-testid={`appointment-block-${a.id}`} onPointerDown={(e) => onPointerDown(e, a, "move")}
      className={`absolute left-1 right-1 z-10 rounded-lg border px-2 py-1 overflow-hidden shadow-sm select-none touch-pan-y ${a._dragging ? "ring-2 ring-stone-900 shadow-lg" : "hover:shadow-md"}`}
      style={{ top: (toMin(a.start) - DAY_START) * PX, height, background: color, borderColor: "rgba(0,0,0,0.08)", opacity: a.status === "completed" ? 0.7 : 1 }}>
      <div className="text-[11px] font-mono font-semibold text-stone-700">{a.start}</div>
      <div className="text-xs font-semibold text-stone-900 truncate leading-tight">{a.client_name}</div>
      <div className="text-[11px] text-stone-600 truncate">{a.service_name}{week ? ` · ${a.staff_name}` : ""}</div>
      <div onPointerDown={(e) => onPointerDown(e, a, "resize")} className="absolute bottom-0 left-0 right-0 h-2.5 cursor-ns-resize" />
    </div>
  );
}

export default function CalendarPage() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const myStaffId = user?.role === "staff" ? user.staff_id : null;
  const [view, setView] = useState("day");
  const [current, setCurrent] = useState(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; });
  const [staff, setStaff] = useState([]);
  const [services, setServices] = useState([]);
  const [clients, setClients] = useState([]);
  const [appts, setAppts] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [dialog, setDialog] = useState(null);
  const [blockInit, setBlockInit] = useState(null);
  const [preview, setPreview] = useState(null); // dragged appointment {id,start,end,date,staff_id}
  const [selection, setSelection] = useState(null); // {ci, s, e}
  const [tick, setTick] = useState(0);
  const [detail, setDetail] = useState(null);
  const [listOpen, setListOpen] = useState(false);
  const gridRef = useRef(null);
  const drag = useRef(null);

  const weekDates = useCallback(() => { const m = mondayOf(current); return Array.from({ length: 7 }, (_, i) => { const d = new Date(m); d.setDate(m.getDate() + i); return d; }); }, [current]);
  const range = view === "day" ? { start: ymd(current), end: ymd(current) } : { start: ymd(weekDates()[0]), end: ymd(weekDates()[6]) };

  const loadMeta = useCallback(async () => {
    const [st, sv, cl] = await Promise.all([api.get("/staff"), api.get("/services"), api.get("/clients")]);
    setStaff(st.data.filter((s) => s.is_active)); setServices(sv.data.filter((s) => s.is_active)); setClients(cl.data); setLoaded(true);
  }, []);
  const loadAppts = useCallback(async () => {
    const [{ data }, bl] = await Promise.all([api.get("/appointments", { params: range }), api.get("/blockouts", { params: range })]);
    setAppts(data); setBlocks(bl.data);
  }, [range.start, range.end]); // eslint-disable-line

  useEffect(() => { loadMeta(); }, [loadMeta]);
  useEffect(() => { loadAppts(); }, [loadAppts, tick]);

  const columns = view === "day"
    ? staff.map((s) => ({ key: s.id, label: s.name, sub: s.role_title, color: s.color, date: ymd(current), staffId: s.id }))
    : weekDates().map((d) => ({ key: ymd(d), label: DAYS_SHORT[(d.getDay() + 6) % 7], sub: `${d.getDate()}/${d.getMonth() + 1}`, date: ymd(d), staffId: null }));

  const shown = appts.map((a) => (preview && a.id === preview.id ? { ...a, ...preview, _dragging: true } : a));
  const apptsFor = (col) => shown.filter((a) => a.date === col.date && (col.staffId ? a.staff_id === col.staffId : true) && a.status !== "cancelled");

  const shadesFor = (col) => {
    const out = blocks.filter((b) => b.start_date <= col.date && b.end_date >= col.date && (!b.staff_id || !col.staffId || b.staff_id === col.staffId))
      .map((b) => ({ key: b.id, block: b, kind: b.kind, staffLevel: !!b.staff_id, s: b.all_day ? DAY_START : toMin(b.start), e: b.all_day ? DAY_END : toMin(b.end),
        label: `${b.reason || (b.staff_id || b.kind === "extra" ? KIND_LABEL[b.kind || "other"] : "Salon closed")}${!col.staffId && b.staff_id ? ` · ${b.staff_name}` : ""}` }));
    const st = col.staffId && staff.find((s) => s.id === col.staffId);
    const wd = String((new Date(`${col.date}T00:00:00`).getDay() + 6) % 7);
    ((st && st.working_hours?.[wd]?.breaks) || []).forEach((br, i) => out.push({ key: `${col.key}-${i}`, kind: "break", s: toMin(br.start), e: toMin(br.end), label: "Break" }));
    return out.filter((x) => x.e > DAY_START && x.s < DAY_END && x.e > x.s);
  };

  const canEditBlock = (b) => isOwner || (b.kind === "extra" && b.staff_id === myStaffId);
  const removeBlock = (b) => setDetail(b);

  const move = (dir) => { const d = new Date(current); d.setDate(d.getDate() + dir * (view === "day" ? 1 : 7)); setCurrent(d); };
  const goToday = () => { const d = new Date(); d.setHours(0, 0, 0, 0); setCurrent(d); };

  const colIndexAt = (clientX) => {
    const rect = gridRef.current.getBoundingClientRect();
    return Math.min(columns.length - 1, Math.max(0, Math.floor((clientX - rect.left) / (rect.width / columns.length))));
  };
  const minAt = (clientY) => DAY_START + (clientY - gridRef.current.getBoundingClientRect().top) / PX;

  // block page scroll on touch once a drag is active
  useEffect(() => {
    const stop = (e) => { if (drag.current?.active) e.preventDefault(); };
    window.addEventListener("touchmove", stop, { passive: false });
    return () => window.removeEventListener("touchmove", stop);
  }, []);

  const activate = () => { const d = drag.current; if (!d || d.active) return; d.active = true; if (navigator.vibrate && d.touch) navigator.vibrate(15); if (d.type === "select") { d.sel = { ci: d.ci, s: d.startMin, e: d.startMin + SNAP }; setSelection(d.sel); } };

  const onMove = (e) => {
    const d = drag.current; if (!d) return;
    const moved = Math.abs(e.clientY - d.y0) + Math.abs(e.clientX - d.x0);
    if (!d.active) {
      if (d.touch) { if (moved > 8) cancelDrag(); return; }
      if (moved < 5) return;
      activate();
    }
    if (d.type === "select") {
      const m = snap(minAt(e.clientY));
      d.sel = { ci: d.ci, s: Math.min(d.startMin, m), e: Math.max(d.startMin + SNAP, m) };
      setSelection(d.sel);
      return;
    }
    const delta = Math.round(((e.clientY - d.y0) / PX) / SNAP) * SNAP;
    if (d.mode === "resize") { d.pv = { id: d.appt.id, end: toHHMM(Math.max(d.s + SNAP, Math.min(DAY_END, d.e + delta))) }; setPreview(d.pv); return; }
    const dur = d.e - d.s;
    const s = Math.max(DAY_START, Math.min(DAY_END - dur, d.s + delta));
    const col = columns[colIndexAt(e.clientX)];
    d.pv = { id: d.appt.id, start: toHHMM(s), end: toHHMM(s + dur), date: col.date, staff_id: col.staffId || d.appt.staff_id };
    setPreview(d.pv);
  };

  const cleanup = () => { window.removeEventListener("pointermove", onMove); window.removeEventListener("pointerup", onUp); window.removeEventListener("pointercancel", cancelDrag); };
  const cancelDrag = () => { const d = drag.current; if (d) clearTimeout(d.timer); drag.current = null; cleanup(); setSelection(null); setPreview(null); };

  const onUp = async () => {
    const d = drag.current; cleanup(); if (!d) return;
    clearTimeout(d.timer); drag.current = null;
    if (!d.active) {
      if (d.type === "appt") setDialog({ initial: d.appt });
      else setDialog({ initial: { date: columns[d.ci].date, start: toHHMM(Math.min(DAY_END - SNAP, d.startMin)), staff_id: columns[d.ci].staffId || staff[0]?.id, status: "booked" } });
      return;
    }
    if (d.type === "select") {
      setSelection(null);
      const sel = d.sel;
      if (sel) {
        const col = columns[sel.ci];
        setBlockInit({ staff_id: isOwner ? col.staffId : myStaffId, start_date: col.date, end_date: col.date, all_day: false, start: toHHMM(sel.s), end: toHHMM(sel.e), kind: "appointment" });
      }
      return;
    }
    const p = d.pv;
    if (!p) { setPreview(null); return; }
    const payload = d.mode === "resize" ? { start: d.appt.start, end: p.end } : { start: p.start, staff_id: p.staff_id, date: p.date };
    try { await withOverride((force) => api.put(`/appointments/${d.appt.id}`, { ...payload, force })); toast.success("Appointment moved"); }
    catch (err) { if (!err.cancelled) toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setTick((t) => t + 1); setPreview(null); }
  };

  const beginDrag = (e, info) => {
    if (e.button && e.button !== 0) return;
    const touch = e.pointerType !== "mouse";
    drag.current = { ...info, x0: e.clientX, y0: e.clientY, touch, active: false };
    if (touch) drag.current.timer = setTimeout(activate, HOLD_MS);
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", cancelDrag);
  };
  const onApptDown = (e, a, mode) => { e.stopPropagation(); beginDrag(e, { type: "appt", appt: a, mode, s: toMin(a.start), e: toMin(a.end) }); };
  const onColDown = (e, ci) => { if (!isOwner && !myStaffId) return; beginDrag(e, { type: "select", ci, startMin: Math.min(DAY_END - SNAP, Math.floor((minAt(e.clientY) - DAY_START) / SNAP) * SNAP + DAY_START) }); };

  const nowMin = new Date().getHours() * 60 + new Date().getMinutes();
  const showNowLine = (col) => col.date === ymd(new Date()) && nowMin >= DAY_START && nowMin <= DAY_END;
  const hours = []; for (let h = DAY_START; h <= DAY_END; h += 60) hours.push(h);
  const colMin = view === "day" ? "min-w-[132px]" : "min-w-[92px]";

  return (
    <div className="max-w-full">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="font-extrabold text-3xl">Calendar</h1>
          <p className="text-stone-600 text-sm mt-1" data-testid="calendar-date-label">{view === "day" ? ukDateLong(current) : `Week of ${ukDateLong(weekDates()[0])}`}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex rounded-full border border-stone-300 overflow-hidden">
            <button data-testid="calendar-view-day-button" onClick={() => setView("day")} className={`px-4 py-2 text-sm ${view === "day" ? "bg-stone-900 text-white" : "bg-white"}`}>Day</button>
            <button data-testid="calendar-view-week-button" onClick={() => setView("week")} className={`px-4 py-2 text-sm ${view === "week" ? "bg-stone-900 text-white" : "bg-white"}`}>Week</button>
          </div>
          <Button variant="outline" size="icon" className="rounded-full h-10 w-10" data-testid="calendar-prev" onClick={() => move(-1)}><ChevronLeft className="h-4 w-4" /></Button>
          <Button variant="outline" size="sm" className="rounded-full h-10" data-testid="calendar-today" onClick={goToday}>Today</Button>
          <Button variant="outline" size="icon" className="rounded-full h-10 w-10" data-testid="calendar-next" onClick={() => move(1)}><ChevronRight className="h-4 w-4" /></Button>
          <div className="flex gap-2 ml-auto md:ml-0">
            {(isOwner || myStaffId) && <Button variant="outline" className="rounded-full h-10" data-testid="time-off-button" onClick={() => setListOpen(true)}><ListX className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">Time off</span></Button>}
            {(isOwner || myStaffId) && <Button variant="outline" className="rounded-full h-10" data-testid="block-out-button" onClick={() => setBlockInit({ staff_id: myStaffId, kind: isOwner ? "holiday" : "extra", all_day: isOwner, start_date: ymd(current), end_date: ymd(current) })}><Ban className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">{isOwner ? "Block out" : "Extra hours"}</span></Button>}
            <Button className="rounded-full h-10 bg-stone-900 hover:bg-stone-800" data-testid="create-appointment-button" onClick={() => setDialog({ initial: { date: ymd(current), start: "09:00", staff_id: staff[0]?.id, status: "booked" } })}><Plus className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">New</span></Button>
          </div>
        </div>
      </div>
      <p className="text-xs text-stone-500 mt-2" data-testid="calendar-hint">Tap a time to book. <span className="md:hidden">Press and hold, then drag, to move a booking or select time to book or block out. Tap a booking or block-out to edit or delete it.</span><span className="hidden md:inline">Drag a booking to move it, or drag across empty time to book or block it out. Click a booking or block-out to edit or delete it.</span></p>

      {!loaded ? (
        <div className="mt-4 bg-white rounded-2xl border border-stone-200 p-8 text-center text-stone-400 text-sm" data-testid="calendar-loading">Loading diary…</div>
      ) : staff.length === 0 ? (
        <div className="mt-4 bg-white rounded-2xl border border-stone-200 p-8 text-center text-stone-500">Add staff and services first to start booking.</div>
      ) : (
        <div className="mt-3 bg-white rounded-2xl border border-stone-200 shadow-sm overflow-hidden">
          <div className="overflow-auto cal-scroll" style={{ maxHeight: "72vh" }} data-testid="calendar-scroll">
            <div className="min-w-full w-max md:w-auto">
              <div className="flex border-b border-stone-200 sticky top-0 bg-white z-20">
                <div className="w-12 shrink-0 border-r border-stone-100 sticky left-0 bg-white z-10" />
                {columns.map((c) => (
                  <div key={c.key} className={`flex-1 ${colMin} px-2 py-2.5 text-center border-r border-stone-100 last:border-r-0`}>
                    <div className="text-sm font-semibold truncate flex items-center justify-center gap-1.5">
                      {view === "day" && <span className="h-2.5 w-2.5 rounded-full inline-block shrink-0" style={{ background: c.color }} />}{c.label}
                    </div>
                    <div className="text-[11px] text-stone-400 truncate">{c.sub}</div>
                  </div>
                ))}
              </div>
              <div className="flex" style={{ height: HEIGHT }}>
                <div className="w-12 shrink-0 border-r border-stone-100 relative sticky left-0 bg-white z-10">
                  {hours.map((h) => <div key={h} className="absolute right-1 text-[10px] font-mono text-stone-400 -translate-y-1/2" style={{ top: (h - DAY_START) * PX }}>{toHHMM(h)}</div>)}
                </div>
                <div ref={gridRef} className="flex-1 flex">
                  {columns.map((col, ci) => (
                    <div key={col.key} data-testid={`calendar-column-${col.key}`} className={`flex-1 ${colMin} relative border-r border-stone-100 last:border-r-0 cursor-pointer select-none touch-pan-x touch-pan-y`} onPointerDown={(e) => onColDown(e, ci)}>
                      {hours.map((h) => <div key={h} className="absolute left-0 right-0 border-t border-stone-100 pointer-events-none" style={{ top: (h - DAY_START) * PX }} />)}
                      {shadesFor(col).map((x) => <Shade key={x.key} x={x} compact={view === "week" && x.staffLevel} onOpen={removeBlock} />)}
                      {selection?.ci === ci && <div data-testid="calendar-selection" className="absolute left-0.5 right-0.5 z-[15] rounded-md bg-stone-900/15 border-2 border-dashed border-stone-900 pointer-events-none text-[11px] font-mono px-1.5 py-0.5" style={clip(selection.s, selection.e)}>{toHHMM(selection.s)}–{toHHMM(selection.e)}</div>}
                      {showNowLine(col) && <div className="absolute left-0 right-0 z-[16] pointer-events-none" style={{ top: (nowMin - DAY_START) * PX }}><div className="h-0.5 bg-rose-500" /><div className="h-2 w-2 rounded-full bg-rose-500 -mt-[5px] -ml-1" /></div>}
                      {apptsFor(col).map((a) => <ApptBlock key={a.id} a={a} color={view === "day" ? col.color : a.staff_color} week={view === "week"} onPointerDown={onApptDown} />)}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {blockInit && <BlockOutDialog open onClose={() => setBlockInit(null)} onSaved={() => setTick((t) => t + 1)} staff={myStaffId ? staff.filter((s) => s.id === myStaffId) : staff} initial={blockInit} ownerMode={isOwner}
        onBook={blockInit.kind === "appointment" ? (init) => { setBlockInit(null); setDialog({ initial: init }); } : undefined} />}

      {detail && <BlockDetailDialog block={detail} canDelete={canEditBlock(detail)} onClose={() => setDetail(null)} onChanged={() => setTick((t) => t + 1)} />}
      {listOpen && <TimeOffListDialog canDelete={canEditBlock} onClose={() => setListOpen(false)} onChanged={() => setTick((t) => t + 1)} />}

      {dialog && (
        <AppointmentDialog open onClose={() => setDialog(null)} onSaved={() => setTick((t) => t + 1)}
          initial={dialog.initial} services={services} staff={staff} clients={clients} />
      )}
    </div>
  );
}
